/**
 * CameraAnnotation — Capture photo + draw annotations.
 * Use case: Insurance Claims — Evidence gathering with damage markup.
 */
import React, { useRef, useState } from 'react';
import { Box, Button, Typography, Stack } from '@mui/material';
import { PhotoCamera as CameraIcon, Draw as DrawIcon, Delete as DeleteIcon } from '@mui/icons-material';

export default function CameraAnnotation({
  value,
  onChange,
  label = 'Capture evidence',
  height = 320,
  ...props
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [imageData, setImageData] = useState(value?.imageData || null);
  const [isDrawing, setIsDrawing] = useState(false);

  const startCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
      }
    } catch (err) {
      console.warn('Camera not available:', err);
      // Fallback: allow file upload
      onChange?.({ target: { value: { imageData: null, error: 'Camera not available' } } });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    stopCamera();
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setImageData(dataUrl);
    onChange?.({ target: { value: { imageData: dataUrl } } });
  };

  const clearPhoto = () => {
    setImageData(null);
    onChange?.({ target: { value: null } });
  };

  React.useEffect(() => () => stopCamera(), []);

  return (
    <Box sx={{ py: 1 }} {...props}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {!imageData ? (
        <>
          {!stream ? (
            <Box
              sx={{
                height,
                border: 2,
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <CameraIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                <Button variant="contained" startIcon={<CameraIcon />} onClick={startCamera}>
                  Open camera
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', borderRadius: 8, maxHeight: height }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
                <Button variant="contained" color="primary" onClick={capturePhoto}>
                  Capture
                </Button>
                <Button variant="outlined" onClick={stopCamera}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <img src={imageData} alt="Captured" style={{ width: '100%', borderRadius: 8, maxHeight: height, objectFit: 'contain' }} />
          <Button size="small" startIcon={<DeleteIcon />} onClick={clearPhoto} sx={{ mt: 1 }}>
            Retake
          </Button>
        </Box>
      )}
    </Box>
  );
}
